import React, { useState, useEffect } from 'react';
import { Package, ChevronRight, CheckCircle, ArrowLeft, Star, Crown, Gem, Baby } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import SEO, { SEO_CONFIG } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getImageUrl, getPlaceholderImage } from '../utils/imageUtils';
import { useCurrency } from '../contexts/CurrencyContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Child size to tier mapping
const CHILD_SIZE_TIER_MAP = {
  '2': '2_4', '4': '2_4',
  '6': '5_8', '8': '5_8',
  '10': '9_12', '12': '9_12',
  'Other': '9_12'  // Default to oldest tier for custom sizes
};

// Child tier labels for display
const CHILD_TIER_LABELS = {
  '2_4': '2-4 yrs',
  '5_8': '5-8 yrs',
  '9_12': '9-12 yrs'
};

// Quality Variants Configuration
const QUALITY_VARIANTS = [
  { 
    id: 'standard', 
    label: 'Standard', 
    description: 'Quality basics for everyday use',
    icon: Star,
    color: 'bg-zinc-100 border-zinc-300 text-zinc-700',
    activeColor: 'bg-zinc-900 border-zinc-900 text-white'
  },
  { 
    id: 'premium', 
    label: 'Premium', 
    description: 'Enhanced quality with better finish',
    icon: Crown,
    color: 'bg-blue-50 border-blue-200 text-blue-700',
    activeColor: 'bg-blue-600 border-blue-600 text-white'
  },
  { 
    id: 'luxury', 
    label: 'Luxury', 
    description: 'Top-tier materials and craftsmanship',
    icon: Gem,
    color: 'bg-amber-50 border-amber-200 text-amber-700',
    activeColor: 'bg-amber-500 border-amber-500 text-white'
  }
];

const BulkOrdersPage = () => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const [step, setStep] = useState(1);
  const [clothingItems, setClothingItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState('standard');
  const [customColor, setCustomColor] = useState('');

  const [orderData, setOrderData] = useState({
    quantity: 50,
    print_type: 'none',
    colors: [],
    sizes: {},
    notes: ''
  });

  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    delivery_address: '',
    delivery_city: '',
    delivery_state: '',
    delivery_country: 'Nigeria'
  });

  const PRINT_OPTIONS = [
    { value: 'none', label: 'No Print', price: 0 },
    { value: 'front', label: 'Front Only', price: 300 },
    { value: 'back', label: 'Back Only', price: 300 },
    { value: 'front_back', label: 'Front & Back', price: 500 },
  ];

  const COLORS = ['Black', 'White', 'Navy', 'Red', 'Grey', 'Blue', 'Green', 'Yellow'];
  
  // Size definitions for each category (Universal Sizes)
  const MALE_SIZES = ['S', 'M', 'L', 'XL', '2XL', 'Other'];
  const FEMALE_SIZES = ['6', '8', '10', '12', '14', 'Other'];
  const CHILD_SIZES = ['2', '4', '6', '8', '10', 'Other'];
  
  // Custom size entries for "Other" option
  const [customFemaleSizes, setCustomFemaleSizes] = useState({});
  const [customMaleSizes, setCustomMaleSizes] = useState({});
  const [customChildSizes, setCustomChildSizes] = useState({});

  // Add custom color
  const addCustomColor = () => {
    const color = customColor.trim();
    if (color && !orderData.colors.includes(color)) {
      setOrderData({...orderData, colors: [...orderData.colors, color]});
      setCustomColor('');
    }
  };

  useEffect(() => {
    loadClothingItems();
  }, []);

  const loadClothingItems = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bulk/clothing-items`);
      setClothingItems(response.data || []);
    } catch (error) {
      console.error('Failed to load items:', error);
    }
  };

  // Get price for current variant (adult sizes)
  const getVariantPrice = (item) => {
    if (!item) return 0;
    switch (selectedVariant) {
      case 'premium':
        return item.premium_price || item.base_price * 1.5;
      case 'luxury':
        return item.luxury_price || item.base_price * 2;
      default:
        return item.standard_price || item.base_price;
    }
  };

  // Get child tier price based on size and variant
  const getChildTierPrice = (item, size) => {
    if (!item) return 0;
    const tier = CHILD_SIZE_TIER_MAP[size] || '9_12';
    const tierPriceKey = `child_price_${tier}`;
    
    // Get base child tier price (stored as standard quality)
    let childTierPrice = item[tierPriceKey];
    
    if (!childTierPrice) {
      // Fallback: use adult price * 0.7 if child price not set
      const adultPrice = getVariantPrice(item);
      childTierPrice = adultPrice * 0.7;
    } else {
      // Apply quality variant multiplier to child price
      if (selectedVariant === 'premium') {
        childTierPrice = childTierPrice * 1.5;
      } else if (selectedVariant === 'luxury') {
        childTierPrice = childTierPrice * 2.0;
      }
    }
    
    return childTierPrice;
  };

  // Get tier label for a child size
  const getChildTierLabel = (size) => {
    const tier = CHILD_SIZE_TIER_MAP[size] || '9_12';
    return CHILD_TIER_LABELS[tier] || '';
  };

  // Handle item selection - auto navigate to next step
  const handleItemSelect = (item) => {
    setSelectedItem(item);
    setSelectedVariant('standard'); // Reset variant on item change
    setTimeout(() => setStep(2), 300);
  };

  // Calculate total with tiered pricing for child sizes
  const calculateTotal = () => {
    if (!selectedItem) return 0;
    const printPrice = PRINT_OPTIONS.find(p => p.value === orderData.print_type)?.price || 0;
    
    // If no sizes breakdown, use simple calculation
    if (getTotalFromSizes() === 0) {
      const unitPrice = getVariantPrice(selectedItem);
      return (unitPrice + printPrice) * orderData.quantity;
    }
    
    // Calculate with tiered pricing
    let total = 0;
    const adultUnitPrice = getVariantPrice(selectedItem);
    
    orderData.colors.forEach(color => {
      const colorSizes = orderData.sizes[color];
      if (!colorSizes) return;
      
      // Adult sizes (male + female) use standard variant pricing
      ['male', 'female'].forEach(gender => {
        if (colorSizes[gender]) {
          Object.entries(colorSizes[gender]).forEach(([size, qty]) => {
            const q = parseInt(qty) || 0;
            total += (adultUnitPrice + printPrice) * q;
          });
        }
      });
      
      // Child sizes use tiered pricing
      if (colorSizes.child) {
        Object.entries(colorSizes.child).forEach(([size, qty]) => {
          const q = parseInt(qty) || 0;
          const childPrice = getChildTierPrice(selectedItem, size);
          total += (childPrice + printPrice) * q;
        });
      }
    });
    
    return total;
  };

  // Get detailed price breakdown for display
  const getPriceBreakdown = () => {
    if (!selectedItem || getTotalFromSizes() === 0) return null;
    
    const printPrice = PRINT_OPTIONS.find(p => p.value === orderData.print_type)?.price || 0;
    const adultUnitPrice = getVariantPrice(selectedItem);
    
    let adultCount = 0;
    let childCounts = { '2_4': 0, '5_8': 0, '9_12': 0 };
    
    orderData.colors.forEach(color => {
      const colorSizes = orderData.sizes[color];
      if (!colorSizes) return;
      
      ['male', 'female'].forEach(gender => {
        if (colorSizes[gender]) {
          Object.values(colorSizes[gender]).forEach(qty => {
            adultCount += parseInt(qty) || 0;
          });
        }
      });
      
      if (colorSizes.child) {
        Object.entries(colorSizes.child).forEach(([size, qty]) => {
          const q = parseInt(qty) || 0;
          const tier = CHILD_SIZE_TIER_MAP[size] || '9_12';
          childCounts[tier] += q;
        });
      }
    });
    
    return {
      adultCount,
      adultUnitPrice: adultUnitPrice + printPrice,
      childCounts,
      childPrices: {
        '2_4': getChildTierPrice(selectedItem, '2') + printPrice,
        '5_8': getChildTierPrice(selectedItem, '6') + printPrice,
        '9_12': getChildTierPrice(selectedItem, '10') + printPrice
      }
    };
  };

  const getUnitPrice = () => {
    if (!selectedItem) return 0;
    const basePrice = getVariantPrice(selectedItem);
    const printPrice = PRINT_OPTIONS.find(p => p.value === orderData.print_type)?.price || 0;
    return basePrice + printPrice;
  };

  // Toggle color selection
  const toggleColor = (color) => {
    const newColors = orderData.colors.includes(color)
      ? orderData.colors.filter(c => c !== color)
      : [...orderData.colors, color];
    
    // Initialize sizes for new color with gender categories
    const newSizes = { ...orderData.sizes };
    if (!newColors.includes(color)) {
      // Removing color, clean up sizes
      delete newSizes[color];
    } else if (!newSizes[color]) {
      // Adding color, initialize structure
      newSizes[color] = {
        male: {},
        female: {},
        child: {}
      };
    }
    
    setOrderData({...orderData, colors: newColors, sizes: newSizes});
  };

  // Update size quantity for specific color and gender
  const updateSizeQty = (color, gender, size, qty) => {
    const newSizes = { ...orderData.sizes };
    if (!newSizes[color]) {
      newSizes[color] = { male: {}, female: {}, child: {} };
    }
    if (!newSizes[color][gender]) {
      newSizes[color][gender] = {};
    }
    
    if (qty > 0) {
      newSizes[color][gender][size] = qty;
    } else {
      delete newSizes[color][gender][size];
    }
    setOrderData({...orderData, sizes: newSizes});
  };

  // Get total for a specific color
  const getColorTotal = (color) => {
    const colorSizes = orderData.sizes[color];
    if (!colorSizes) return 0;
    
    let total = 0;
    ['male', 'female', 'child'].forEach(gender => {
      if (colorSizes[gender]) {
        Object.values(colorSizes[gender]).forEach(qty => {
          total += parseInt(qty) || 0;
        });
      }
    });
    return total;
  };

  // Calculate total from all sizes (all colors)
  const getTotalFromSizes = () => {
    let total = 0;
    orderData.colors.forEach(color => {
      total += getColorTotal(color);
    });
    return total;
  };

  // Check if all colors meet minimum quantity
  const validateColorMinimums = () => {
    const errors = [];
    orderData.colors.forEach(color => {
      const colorTotal = getColorTotal(color);
      if (colorTotal > 0 && colorTotal < 50) {
        errors.push(`${color}: ${colorTotal}/50 pieces (need ${50 - colorTotal} more)`);
      }
    });
    return errors;
  };

  const handleSubmit = async () => {
    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
      toast.error('Please fill in all contact details');
      return;
    }

    // Validate minimum per color
    const colorErrors = validateColorMinimums();
    if (colorErrors.length > 0) {
      toast.error(`Minimum 50 pieces required per color:\n${colorErrors.join('\n')}`);
      return;
    }

    // Navigate to order details page for review and payment
    navigate('/bulk-orders/details', {
      state: {
        orderData: {
          ...orderData,
          quantity: getTotalFromSizes() > 0 ? getTotalFromSizes() : orderData.quantity,
          product_variant: selectedVariant,
          unit_price: getUnitPrice(),
          custom_male_sizes: customMaleSizes,
          custom_female_sizes: customFemaleSizes,
          custom_child_sizes: customChildSizes
        },
        customerInfo,
        selectedItem,
        selectedVariant
      }
    });
  };

  const steps = [
    { num: 1, label: 'Select Item' },
    { num: 2, label: 'Customize' },
    { num: 3, label: 'Contact Info' },
  ];

  return (
    <div className="min-h-screen bg-zinc-50">
      <SEO 
        title={SEO_CONFIG.bulkOrders.title}
        description={SEO_CONFIG.bulkOrders.description}
        image={SEO_CONFIG.bulkOrders.image}
        url={SEO_CONFIG.bulkOrders.url}
      />

      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center text-zinc-500 hover:text-zinc-900 mb-4 text-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Home
          </button>
          <h1 className="text-3xl md:text-4xl font-bold text-zinc-900">{SEO_CONFIG.bulkOrders.h1}</h1>
          <p className="text-zinc-600 mt-2">Custom uniforms for schools, corporates & events</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {steps.map((s, idx) => (
              <div key={s.num} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${
                  step >= s.num ? 'bg-[#D90429] text-white' : 'bg-zinc-200 text-zinc-500'
                }`}>
                  {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
                </div>
                <span className={`ml-2 text-sm hidden sm:inline ${step >= s.num ? 'text-zinc-900' : 'text-zinc-500'}`}>
                  {s.label}
                </span>
                {idx < steps.length - 1 && (
                  <ChevronRight className="w-5 h-5 text-zinc-300 mx-4" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Step 1: Select Item */}
        {step === 1 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-zinc-900">Select Clothing Item</h2>
            <p className="text-zinc-500">Click on an item to continue</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {clothingItems.map((item) => (
                <Card 
                  key={item.id}
                  className={`cursor-pointer transition-all hover:shadow-lg hover:scale-105 ${
                    selectedItem?.id === item.id ? 'ring-2 ring-[#D90429]' : ''
                  }`}
                  onClick={() => handleItemSelect(item)}
                  data-testid={`bulk-item-${item.id}`}
                >
                  {/* ISSUE 2 FIX: Use object-contain to show full outfit without cropping */}
                  <div className="aspect-square bg-white rounded-t-lg overflow-hidden flex items-center justify-center">
                    <img 
                      src={getImageUrl(item.image_url)} 
                      alt={item.name}
                      className="w-full h-full object-contain object-center"
                      onError={(e) => { e.target.src = getPlaceholderImage(item.name); }}
                    />
                  </div>
                  <CardContent className="p-3">
                    <p className="font-medium text-sm">{item.name}</p>
                    <p className="text-[#D90429] font-semibold">
                      From {formatPrice(item.standard_price || item.base_price)}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">3 quality options</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Customize */}
        {step === 2 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-zinc-900">Customize Your Order</h2>
            
            <Card>
              <CardContent className="p-6 space-y-6">
                {/* ISSUE 3 FIX: Large preview container for variation selection */}
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Large Image Preview */}
                  <div className="lg:w-1/2">
                    <div className="max-h-[450px] w-full flex justify-center items-center bg-white rounded-lg border p-4">
                      <img 
                        src={getImageUrl(selectedItem?.image_url)}
                        alt={selectedItem?.name}
                        className="object-contain max-h-[420px] w-auto"
                        onError={(e) => { e.target.src = getPlaceholderImage(selectedItem?.name, 400, 450); }}
                      />
                    </div>
                    <div className="mt-3 text-center">
                      <p className="font-semibold text-lg">{selectedItem?.name}</p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setStep(1)}
                        className="mt-2"
                      >
                        Change Item
                      </Button>
                    </div>
                  </div>
                  
                  {/* Selection Panel */}
                  <div className="lg:w-1/2 space-y-6">
                    {/* Quality Variant Selector */}
                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-3">
                        Select Quality Variant *
                      </label>
                      <div className="grid grid-cols-1 gap-3">
                        {QUALITY_VARIANTS.map((variant) => {
                          const Icon = variant.icon;
                          const price = selectedItem ? (
                            variant.id === 'premium' ? selectedItem.premium_price :
                            variant.id === 'luxury' ? selectedItem.luxury_price :
                            selectedItem.standard_price
                          ) || selectedItem.base_price : 0;
                      
                      const isSelected = selectedVariant === variant.id;
                      
                      return (
                        <button
                          key={variant.id}
                          onClick={() => setSelectedVariant(variant.id)}
                          className={`relative p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                            isSelected ? variant.activeColor : variant.color
                          } hover:scale-[1.02]`}
                          data-testid={`variant-${variant.id}`}
                        >
                          {isSelected && (
                            <div className="absolute -top-2 -right-2">
                              <CheckCircle className="w-6 h-6 text-white bg-green-500 rounded-full" />
                            </div>
                          )}
                          <div className="flex items-center gap-2 mb-2">
                            <Icon className="w-5 h-5" />
                            <span className="font-bold">{variant.label}</span>
                          </div>
                          <p className={`text-xs mb-2 ${isSelected ? 'opacity-90' : 'opacity-70'}`}>
                            {variant.description}
                          </p>
                          <p className="text-xl font-bold">
                            {formatPrice(price)}
                            <span className="text-xs font-normal opacity-70">/piece</span>
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Quick Quantity or use Size Breakdown below */}
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Total Quantity (minimum 50) - Or use size breakdown below
                  </label>
                  <input
                    type="number"
                    min="50"
                    value={getTotalFromSizes() > 0 ? getTotalFromSizes() : orderData.quantity}
                    onChange={(e) => setOrderData({...orderData, quantity: Math.max(50, parseInt(e.target.value) || 50)})}
                    className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429] focus:border-transparent"
                    disabled={getTotalFromSizes() > 0}
                  />
                  {getTotalFromSizes() > 0 && (
                    <p className="text-xs text-zinc-500 mt-1">Quantity calculated from size breakdown</p>
                  )}
                </div>

                {/* Print Type */}
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Print Option
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {PRINT_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setOrderData({...orderData, print_type: opt.value})}
                        className={`p-3 border rounded-lg text-left transition-all ${
                          orderData.print_type === opt.value 
                            ? 'border-[#D90429] bg-red-50' 
                            : 'border-zinc-200 hover:border-zinc-300'
                        }`}
                      >
                        <p className="font-medium text-sm">{opt.label}</p>
                        <p className="text-xs text-zinc-500">+{formatPrice(opt.price)}/piece</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Colors */}
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Select Colors (click to toggle)
                  </label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {COLORS.map((color) => (
                      <button
                        key={color}
                        onClick={() => toggleColor(color)}
                        className={`px-4 py-2 rounded-full text-sm border transition-all ${
                          orderData.colors.includes(color)
                            ? 'bg-zinc-900 text-white border-zinc-900'
                            : 'bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400'
                        }`}
                        data-testid={`color-${color}`}
                      >
                        {orderData.colors.includes(color) && '✓ '}{color}
                      </button>
                    ))}
                  </div>
                  {/* Other Color Input */}
                  <div className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={customColor}
                      onChange={(e) => setCustomColor(e.target.value)}
                      placeholder="Other color..."
                      className="flex-1 px-3 py-2 text-sm border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429] focus:border-transparent"
                      onKeyPress={(e) => e.key === 'Enter' && addCustomColor()}
                      data-testid="custom-color-input"
                    />
                    <button
                      type="button"
                      onClick={addCustomColor}
                      disabled={!customColor.trim()}
                      className="px-4 py-2 text-sm bg-zinc-100 text-zinc-700 rounded-lg hover:bg-zinc-200 disabled:opacity-50"
                    >
                      Add
                    </button>
                  </div>
                  {orderData.colors.length > 0 && (
                    <p className="text-sm text-zinc-500 mt-2">
                      Selected: {orderData.colors.join(', ')}
                    </p>
                  )}
                </div>

                {/* Size Breakdown */}
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Size Distribution (Total: {getTotalFromSizes()} pieces)
                  </label>
                  
                  {/* Size breakdown by color with Male/Female/Child sections */}
                  {orderData.colors.length > 0 ? (
                    <div className="space-y-6">
                      {orderData.colors.map(color => (
                        <div key={color} className="bg-zinc-50 p-4 rounded-lg border border-zinc-200">
                          <div className="flex justify-between items-center mb-4">
                            <h4 className="font-semibold text-lg flex items-center gap-2">
                              <span 
                                className="w-4 h-4 rounded-full border"
                                style={{ backgroundColor: color.toLowerCase() }}
                              ></span>
                              {color}
                            </h4>
                            <div className="text-sm">
                              <span className={`font-medium ${getColorTotal(color) >= 50 ? 'text-green-600' : 'text-amber-600'}`}>
                                {getColorTotal(color)} / 50 pieces
                              </span>
                              {getColorTotal(color) > 0 && getColorTotal(color) < 50 && (
                                <span className="text-red-500 ml-2">Need {50 - getColorTotal(color)} more</span>
                              )}
                            </div>
                          </div>
                          
                          {/* Male Sizes */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-zinc-700 mb-2">Male Sizes</label>
                            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                              {MALE_SIZES.map(size => (
                                <div key={`male-${size}`} className="text-center">
                                  <label className="block text-xs font-medium text-zinc-500 mb-1">{size}</label>
                                  {size === 'Other' ? (
                                    <div className="space-y-1">
                                      <input
                                        type="number"
                                        min="0"
                                        value={orderData.sizes[color]?.male?.[size] || ''}
                                        onChange={(e) => updateSizeQty(color, 'male', size, parseInt(e.target.value) || 0)}
                                        placeholder="Qty"
                                        className="w-full px-2 py-2 text-center border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429] focus:border-transparent text-sm"
                                        data-testid={`${color}-male-${size}`}
                                      />
                                      {(orderData.sizes[color]?.male?.[size] || 0) > 0 && (
                                        <input
                                          type="text"
                                          value={customMaleSizes[color] || ''}
                                          onChange={(e) => setCustomMaleSizes({...customMaleSizes, [color]: e.target.value})}
                                          placeholder="Custom size"
                                          className="w-full px-2 py-1 text-center border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent text-xs bg-amber-50"
                                          data-testid={`${color}-male-custom-size`}
                                        />
                                      )}
                                    </div>
                                  ) : (
                                    <input
                                      type="number"
                                      min="0"
                                      value={orderData.sizes[color]?.male?.[size] || ''}
                                      onChange={(e) => updateSizeQty(color, 'male', size, parseInt(e.target.value) || 0)}
                                      placeholder="0"
                                      className="w-full px-2 py-2 text-center border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429] focus:border-transparent text-sm"
                                      data-testid={`${color}-male-${size}`}
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Female Sizes */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-zinc-700 mb-2">Female Sizes</label>
                            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                              {FEMALE_SIZES.map(size => (
                                <div key={`female-${size}`} className="text-center">
                                  <label className="block text-xs font-medium text-zinc-500 mb-1">{size}</label>
                                  {size === 'Other' ? (
                                    <div className="space-y-1">
                                      <input
                                        type="number"
                                        min="0"
                                        value={orderData.sizes[color]?.female?.[size] || ''}
                                        onChange={(e) => updateSizeQty(color, 'female', size, parseInt(e.target.value) || 0)}
                                        placeholder="Qty"
                                        className="w-full px-2 py-2 text-center border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429] focus:border-transparent text-sm"
                                        data-testid={`${color}-female-${size}`}
                                      />
                                      {(orderData.sizes[color]?.female?.[size] || 0) > 0 && (
                                        <input
                                          type="text"
                                          value={customFemaleSizes[color] || ''}
                                          onChange={(e) => setCustomFemaleSizes({...customFemaleSizes, [color]: e.target.value})}
                                          placeholder="Custom size"
                                          className="w-full px-2 py-1 text-center border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent text-xs bg-amber-50"
                                          data-testid={`${color}-female-custom-size`}
                                        />
                                      )}
                                    </div>
                                  ) : (
                                    <input
                                      type="number"
                                      min="0"
                                      value={orderData.sizes[color]?.female?.[size] || ''}
                                      onChange={(e) => updateSizeQty(color, 'female', size, parseInt(e.target.value) || 0)}
                                      placeholder="0"
                                      className="w-full px-2 py-2 text-center border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429] focus:border-transparent text-sm"
                                      data-testid={`${color}-female-${size}`}
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Child Sizes with Tiered Pricing */}
                          <div>
                            <label className="block text-sm font-medium text-zinc-700 mb-1">Child Sizes</label>
                            {/* Child tier pricing info */}
                            <div className="flex items-center gap-2 mb-2 p-2 bg-purple-50 rounded-lg">
                              <Baby className="w-4 h-4 text-purple-600" />
                              <span className="text-xs text-purple-700">
                                Tiered pricing: 
                                <span className="font-medium ml-1">2-4 yrs: {formatPrice(Math.round(getChildTierPrice(selectedItem, '2')))}</span>
                                <span className="mx-1">|</span>
                                <span className="font-medium">5-8 yrs: {formatPrice(Math.round(getChildTierPrice(selectedItem, '6')))}</span>
                                <span className="mx-1">|</span>
                                <span className="font-medium">9-12 yrs: {formatPrice(Math.round(getChildTierPrice(selectedItem, '10')))}</span>
                              </span>
                            </div>
                            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                              {CHILD_SIZES.map(size => (
                                <div key={`child-${size}`} className="text-center">
                                  <label className="block text-xs font-medium text-zinc-500 mb-0.5">
                                    {size}{size !== 'Other' && ' yrs'}
                                  </label>
                                  {size !== 'Other' && (
                                    <span className="block text-xs text-purple-600 mb-1">
                                      {getChildTierLabel(size)}
                                    </span>
                                  )}
                                  {size === 'Other' ? (
                                    <div className="space-y-1">
                                      <input
                                        type="number"
                                        min="0"
                                        value={orderData.sizes[color]?.child?.[size] || ''}
                                        onChange={(e) => updateSizeQty(color, 'child', size, parseInt(e.target.value) || 0)}
                                        placeholder="Qty"
                                        className="w-full px-2 py-2 text-center border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429] focus:border-transparent text-sm"
                                        data-testid={`${color}-child-${size}`}
                                      />
                                      {(orderData.sizes[color]?.child?.[size] || 0) > 0 && (
                                        <input
                                          type="text"
                                          value={customChildSizes[color] || ''}
                                          onChange={(e) => setCustomChildSizes({...customChildSizes, [color]: e.target.value})}
                                          placeholder="Custom size"
                                          className="w-full px-2 py-1 text-center border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent text-xs bg-amber-50"
                                          data-testid={`${color}-child-custom-size`}
                                        />
                                      )}
                                    </div>
                                  ) : (
                                    <input
                                      type="number"
                                      min="0"
                                      value={orderData.sizes[color]?.child?.[size] || ''}
                                      onChange={(e) => updateSizeQty(color, 'child', size, parseInt(e.target.value) || 0)}
                                      placeholder="0"
                                      className="w-full px-2 py-2 text-center border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429] focus:border-transparent text-sm"
                                      data-testid={`${color}-child-${size}`}
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {/* Validation Summary */}
                      {validateColorMinimums().length > 0 && (
                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-amber-700 font-medium mb-2">Minimum 50 pieces required per color:</p>
                          <ul className="text-amber-600 text-sm list-disc list-inside">
                            {validateColorMinimums().map((err, i) => (
                              <li key={i}>{err}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-zinc-50 rounded-lg border-2 border-dashed border-zinc-300">
                      <p className="text-zinc-500">Select colors above to add size quantities</p>
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Special Instructions (optional)
                  </label>
                  <textarea
                    value={orderData.notes}
                    onChange={(e) => setOrderData({...orderData, notes: e.target.value})}
                    rows={3}
                    className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429] focus:border-transparent"
                    placeholder="Any specific requirements..."
                  />
                </div>
                  </div>{/* End lg:w-1/2 Selection Panel */}
                </div>{/* End flex container */}
              </CardContent>
            </Card>

            {/* Order Summary with Variant and Child Tier Pricing */}
            <Card className="bg-zinc-900 text-white">
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-zinc-400 text-sm">Quality:</p>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        selectedVariant === 'luxury' ? 'bg-amber-500' :
                        selectedVariant === 'premium' ? 'bg-blue-500' : 'bg-zinc-600'
                      }`}>
                        {selectedVariant.charAt(0).toUpperCase() + selectedVariant.slice(1)}
                      </span>
                    </div>
                    <p className="text-zinc-400 text-sm">
                      Adult Unit Price: {formatPrice(getUnitPrice())}
                    </p>
                    
                    {/* Child tier pricing breakdown */}
                    {getPriceBreakdown() && (
                      <div className="mt-2 pt-2 border-t border-zinc-700">
                        <p className="text-zinc-300 text-xs font-medium mb-1">Price Breakdown:</p>
                        {getPriceBreakdown().adultCount > 0 && (
                          <p className="text-zinc-400 text-xs">
                            Adults ({getPriceBreakdown().adultCount}): {formatPrice(getPriceBreakdown().adultUnitPrice)} each
                          </p>
                        )}
                        {getPriceBreakdown().childCounts['2_4'] > 0 && (
                          <p className="text-purple-400 text-xs flex items-center gap-1">
                            <Baby className="w-3 h-3" />
                            2-4 yrs ({getPriceBreakdown().childCounts['2_4']}): {formatPrice(Math.round(getPriceBreakdown().childPrices['2_4']))} each
                          </p>
                        )}
                        {getPriceBreakdown().childCounts['5_8'] > 0 && (
                          <p className="text-purple-400 text-xs flex items-center gap-1">
                            <Baby className="w-3 h-3" />
                            5-8 yrs ({getPriceBreakdown().childCounts['5_8']}): {formatPrice(Math.round(getPriceBreakdown().childPrices['5_8']))} each
                          </p>
                        )}
                        {getPriceBreakdown().childCounts['9_12'] > 0 && (
                          <p className="text-purple-400 text-xs flex items-center gap-1">
                            <Baby className="w-3 h-3" />
                            9-12 yrs ({getPriceBreakdown().childCounts['9_12']}): {formatPrice(Math.round(getPriceBreakdown().childPrices['9_12']))} each
                          </p>
                        )}
                      </div>
                    )}
                    
                    <p className="text-zinc-400 text-sm">
                      Total Quantity: {getTotalFromSizes() > 0 ? getTotalFromSizes() : orderData.quantity} pieces
                    </p>
                    <p className="text-3xl font-bold mt-2">{formatPrice(Math.round(calculateTotal()))}</p>
                  </div>
                  <Package className="w-12 h-12 text-zinc-600" />
                </div>
              </CardContent>
            </Card>

            {/* Validation message for sizes */}
            {getTotalFromSizes() > 0 && getTotalFromSizes() !== orderData.quantity && orderData.quantity > 0 && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-700">
                ⚠️ Total quantity must equal selected sizes. Current: {getTotalFromSizes()} pieces, Expected: {orderData.quantity} pieces.
              </div>
            )}

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button 
                onClick={() => setStep(3)}
                className="bg-[#D90429] hover:bg-[#B90322]"
                disabled={orderData.colors.length === 0 || validateColorMinimums().length > 0}
              >
                Continue <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Contact Info */}
        {step === 3 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-zinc-900">Contact & Delivery Information</h2>
            
            <Card>
              <CardContent className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">Full Name *</label>
                    <input
                      type="text"
                      value={customerInfo.name}
                      onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                      className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429] focus:border-transparent"
                      placeholder="Your name"
                      data-testid="bulk-customer-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">Email *</label>
                    <input
                      type="email"
                      value={customerInfo.email}
                      onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                      className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429] focus:border-transparent"
                      placeholder="your@email.com"
                      data-testid="bulk-customer-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      value={customerInfo.phone}
                      onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                      className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429] focus:border-transparent"
                      placeholder="+234..."
                      data-testid="bulk-customer-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">Delivery Address</label>
                    <input
                      type="text"
                      value={customerInfo.delivery_address}
                      onChange={(e) => setCustomerInfo({...customerInfo, delivery_address: e.target.value})}
                      className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429] focus:border-transparent"
                      placeholder="Street address"
                      data-testid="bulk-delivery-address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">City</label>
                    <input
                      type="text"
                      value={customerInfo.delivery_city}
                      onChange={(e) => setCustomerInfo({...customerInfo, delivery_city: e.target.value})}
                      className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429] focus:border-transparent"
                      placeholder="Lagos"
                      data-testid="bulk-delivery-city"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">State / Province</label>
                    <input
                      type="text"
                      value={customerInfo.delivery_state}
                      onChange={(e) => setCustomerInfo({...customerInfo, delivery_state: e.target.value})}
                      className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429] focus:border-transparent"
                      placeholder="Lagos State"
                      data-testid="bulk-delivery-state"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 mb-2">Country</label>
                    <input
                      type="text"
                      value={customerInfo.delivery_country}
                      onChange={(e) => setCustomerInfo({...customerInfo, delivery_country: e.target.value})}
                      className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429] focus:border-transparent"
                      placeholder="Nigeria"
                      data-testid="bulk-delivery-country"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Final Summary with Variant and Child Tier Pricing */}
            <Card className="bg-zinc-900 text-white">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Item</span>
                    <span>{selectedItem?.name}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400">Quality</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      selectedVariant === 'luxury' ? 'bg-amber-500' :
                      selectedVariant === 'premium' ? 'bg-blue-500' : 'bg-zinc-600'
                    }`}>
                      {selectedVariant.charAt(0).toUpperCase() + selectedVariant.slice(1)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Adult Unit Price</span>
                    <span>{formatPrice(getUnitPrice())}</span>
                  </div>
                  
                  {/* Child tier pricing in final summary */}
                  {getPriceBreakdown() && (getPriceBreakdown().childCounts['2_4'] > 0 || getPriceBreakdown().childCounts['5_8'] > 0 || getPriceBreakdown().childCounts['9_12'] > 0) && (
                    <div className="py-2 border-y border-zinc-700 my-2">
                      <p className="text-purple-400 text-xs font-medium mb-1 flex items-center gap-1">
                        <Baby className="w-3 h-3" /> Child Tier Pricing:
                      </p>
                      {getPriceBreakdown().childCounts['2_4'] > 0 && (
                        <div className="flex justify-between text-xs">
                          <span className="text-zinc-500">Ages 2-4 ({getPriceBreakdown().childCounts['2_4']} pcs)</span>
                          <span className="text-purple-400">{formatPrice(Math.round(getPriceBreakdown().childPrices['2_4']))} each</span>
                        </div>
                      )}
                      {getPriceBreakdown().childCounts['5_8'] > 0 && (
                        <div className="flex justify-between text-xs">
                          <span className="text-zinc-500">Ages 5-8 ({getPriceBreakdown().childCounts['5_8']} pcs)</span>
                          <span className="text-purple-400">{formatPrice(Math.round(getPriceBreakdown().childPrices['5_8']))} each</span>
                        </div>
                      )}
                      {getPriceBreakdown().childCounts['9_12'] > 0 && (
                        <div className="flex justify-between text-xs">
                          <span className="text-zinc-500">Ages 9-12 ({getPriceBreakdown().childCounts['9_12']} pcs)</span>
                          <span className="text-purple-400">{formatPrice(Math.round(getPriceBreakdown().childPrices['9_12']))} each</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Quantity</span>
                    <span>{getTotalFromSizes() > 0 ? getTotalFromSizes() : orderData.quantity} pieces</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Print</span>
                    <span>{PRINT_OPTIONS.find(p => p.value === orderData.print_type)?.label}</span>
                  </div>
                  {orderData.colors.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Colors</span>
                      <span>{orderData.colors.join(', ')}</span>
                    </div>
                  )}
                  <div className="border-t border-zinc-700 my-3"></div>
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span>{formatPrice(Math.round(calculateTotal()))}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button 
                onClick={handleSubmit}
                className="bg-[#D90429] hover:bg-[#B90322]"
              >
                Review Order <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkOrdersPage;
